import { useState, useEffect } from 'react';

const NAV_LINKS = [
  { label: 'About', href: '#about' },
  { label: 'Products', href: '#products' },
  { label: 'Features', href: '#features' },
  { label: 'Contact', href: '#contact' },
];

export default function HeaderSection() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNav = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
    setMenuOpen(false);
  };

  return (
    <section id="header" className="relative min-h-screen flex flex-col overflow-hidden">
      {/* Background image */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://public.readdy.ai/ai/img_res/9210d7b24857c61b35787cfe03251722.jpg"
          alt="Hero background"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/80" />
      </div>

      {/* Navbar */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between h-16">
          {/* Logo + Name */}
          <a
            href="#header"
            onClick={(e) => handleNav(e, '#header')}
            className="flex items-center gap-3 cursor-pointer"
          >
            <img
              src="https://public.readdy.ai/ai/img_res/9ed2ffaa-9a18-4b65-82e1-771deaf8a334.png"
              alt="AMIK P.C. Logo"
              className="h-9 w-auto object-contain"
            />
            <span
              className={`font-bold text-lg tracking-widest whitespace-nowrap transition-colors duration-300 ${
                scrolled ? 'text-gray-900' : 'text-white'
              }`}
            >
              AMIK P.C.
            </span>
          </a>

          {/* Desktop Nav */}
          <ul className="hidden md:flex items-center gap-8">
            {NAV_LINKS.map((link) => (
              <li key={link.label}>
                <a
                  href={link.href}
                  onClick={(e) => handleNav(e, link.href)}
                  className={`text-sm font-medium tracking-wide transition-colors duration-300 cursor-pointer hover:opacity-70 ${
                    scrolled ? 'text-gray-700' : 'text-white/90'
                  }`}
                >
                  {link.label}
                </a>
              </li>
            ))}
          </ul>

          {/* Mobile hamburger */}
          <button
            className={`md:hidden w-8 h-8 flex flex-col items-center justify-center gap-1.5 cursor-pointer ${
              scrolled ? 'text-gray-900' : 'text-white'
            }`}
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            <span className={`block w-5 h-0.5 bg-current transition-all ${menuOpen ? 'rotate-45 translate-y-2' : ''}`} />
            <span className={`block w-5 h-0.5 bg-current transition-all ${menuOpen ? 'opacity-0' : ''}`} />
            <span className={`block w-5 h-0.5 bg-current transition-all ${menuOpen ? '-rotate-45 -translate-y-2' : ''}`} />
          </button>
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 px-6 py-4 flex flex-col gap-4">
            {NAV_LINKS.map((link) => (
              <a
                key={link.label}
                href={link.href}
                onClick={(e) => handleNav(e, link.href)}
                className="text-gray-700 font-medium text-sm cursor-pointer hover:text-gray-900"
              >
                {link.label}
              </a>
            ))}
          </div>
        )}
      </nav>

      {/* Hero Content */}
      <div className="relative z-10 flex-1 flex flex-col items-center justify-center text-center px-6 pt-24 pb-20 w-full">
        <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-1.5 mb-8">
          <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
          <span className="text-white/80 text-xs tracking-widest uppercase font-medium">iOS App Development</span>
        </div>

        <h1 className="text-5xl md:text-7xl font-extrabold text-white leading-tight mb-6 max-w-4xl">
          Building the Future<br />
          <span className="italic font-light text-emerald-300">of iOS Experiences</span>
        </h1>

        <p className="text-white/60 text-base md:text-lg tracking-widest font-light mb-16 max-w-xl">
          Security &nbsp;·&nbsp; Performance &nbsp;·&nbsp; Simplicity
        </p>

        {/* App mockup cards */}
        <div className="flex items-end justify-center gap-4 md:gap-6 mt-4">
          {/* Left card */}
          <div className="hidden sm:block w-36 md:w-44 rounded-2xl overflow-hidden shadow-2xl rotate-[-6deg] translate-y-4 opacity-80">
            <img
              src="https://readdy.ai/api/search-image?query=iOS%20VPN%20mobile%20app%20interface%20screenshot%20on%20dark%20navy%20background%2C%20shield%20icon%2C%20connection%20status%20UI%2C%20clean%20modern%20design%2C%20green%20accent%20colors%2C%20minimal%20flat%20UI%20mockup%2C%20portrait%20orientation&width=300&height=540&seq=amik-vpn-card-left&orientation=portrait"
              alt="VPN App"
              className="w-full h-full object-cover object-top"
            />
          </div>
          {/* Center card */}
          <div className="w-44 md:w-56 rounded-2xl overflow-hidden shadow-2xl z-10">
            <img
              src="https://readdy.ai/api/search-image?query=iOS%20mobile%20app%20home%20screen%20interface%20with%20clean%20dashboard%2C%20dark%20mode%20UI%2C%20security%20and%20performance%20metrics%2C%20modern%20minimalist%20design%2C%20emerald%20green%20and%20white%20accents%2C%20professional%20app%20mockup%2C%20portrait%20orientation&width=360&height=640&seq=amik-center-card&orientation=portrait"
              alt="App Interface"
              className="w-full h-full object-cover object-top"
            />
          </div>
          {/* Right card */}
          <div className="hidden sm:block w-36 md:w-44 rounded-2xl overflow-hidden shadow-2xl rotate-[6deg] translate-y-4 opacity-80">
            <img
              src="https://readdy.ai/api/search-image?query=iOS%20cleaner%20app%20interface%20screenshot%20on%20light%20beige%20background%2C%20storage%20optimization%20UI%2C%20circular%20progress%20indicator%2C%20clean%20modern%20design%2C%20warm%20tones%2C%20minimal%20flat%20UI%20mockup%2C%20portrait%20orientation&width=300&height=540&seq=amik-cleaner-card-right&orientation=portrait"
              alt="Cleaner App"
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="relative z-10 flex justify-center pb-8 animate-bounce">
        <div className="w-6 h-10 rounded-full border-2 border-white/40 flex items-start justify-center pt-2">
          <span className="w-1 h-2 bg-white/60 rounded-full" />
        </div>
      </div>
    </section>
  );
}
